import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import {
  useSwrInstantlyActivateJob,
  useSwrSuspendJob,
} from '@hooks/useSwrApi/endpoints/my-jobs';
import { useBjModal } from '@hooks/index';
import { useSwrTopJobSuspend } from '@hooks/useSwrApi/endpoints/job/useSwrTopJobSuspend';

type UseCompanyJobActionsProps = {
  jobSlug: string;
  workplaceJobSlug?: string;
  workplaceSlug?: string;
  eligibleForInstantActivation?: boolean;
  onSuspension?: () => void;
  onActivation?: () => void;
  onTopJobSuspension?: () => void;
};

/**
 * @description useCompanyJobActions is a custom hook that provides functions to handle company job actions, such as activating and suspending jobs.
 * It uses SWR for data fetching and mutation, and it integrates with the router for navigation.
 * It also uses a custom modal hook (`useBjModal`) to manage the suspension confirmation modal state.
 *
 * Props:
 * * **jobSlug** - string - The slug of the job to be activated or suspended.
 * * **workplaceJobSlug** - string - Optional slug of the workplace job.
 * * **workplaceSlug** - string - Optional slug of the workplace.
 * * **eligibleForInstantActivation** - boolean - Optional flag indicating if the job is eligible for instant activation.
 * * **onSuspension** - function - Optional callback function that is called when the job suspension is successful.
 * * **onActivation** - function - Optional callback function that is called when the job activation is successful.
 * * **onTopJobSuspension** - function - Optional callback function that is called when the top job suspension is successful.
 *
 * Returns:
 * * **handleJobActivation** - function to handle job activation, if instant activation is available it triggers SWR mutation and redirects
 * to after-activation page on success, otherwise it redirects to the edit page.
 * * **handleJobSuspension** - function to handle job suspension, opens a confirmation modal.
 * * **handleTopJobSuspension** - function to handle top job suspension, triggers the SWR mutation for suspending a top job.
 * * **handleSuspensionConfirmation** - function to confirm job suspension, triggers the suspension SWR mutation.
 * * **handleSuspensionCancellation** - function to cancel job suspension, closes the confirmation modal.
 * * **isActivating** - boolean value indicating if the job activation is in progress.
 * * **isSuspending** - boolean value indicating if the job suspension is in progress.
 * * **isSuspendingTopJob** - boolean value indicating if the top job suspension is in progress.
 * * **isSuspensionConfirmationOpen** - boolean value indicating if the suspension confirmation modal is open.
 * * **openSuspensionConfirmationModal** - function to open the suspension confirmation modal.
 * * **closeSuspensionConfirmationModal** - function to close the suspension confirmation modal.
 *
 * @example
 * ```tsx
 * const { handleJobActivation, handleJobSuspension, isActivating, isSuspending } = useCompanyJobActions({ job });
 * ```
 */

export function useCompanyJobActions(props: UseCompanyJobActionsProps) {
  const {
    jobSlug,
    workplaceJobSlug,
    workplaceSlug,
    eligibleForInstantActivation,
    onSuspension,
    onActivation,
    onTopJobSuspension,
  } = props;
  const { router } = useExtendedRouter();

  const {
    isOpen: isSuspensionConfirmationOpen,
    openModal: openSuspensionConfirmationModal,
    closeModal: closeSuspensionConfirmationModal,
  } = useBjModal();

  // SWR: Instantly activate a job
  const { trigger: instantlyActivateJob, isMutating: isActivating } = useSwrInstantlyActivateJob({
    jobSlug,
    onSuccess: () => {
      router.push(`/after-activation/${jobSlug}`);
      onActivation?.();
    },
  });

  // SWR: Suspend a job
  const { trigger: suspendJob, isMutating: isSuspending } = useSwrSuspendJob({
    jobSlug,
    onSuccess: () => {
      closeSuspensionConfirmationModal();
      onSuspension?.();
    },
  });

  // SWR: Suspend a top job
  const { trigger: suspendTopJob, isMutating: isSuspendingTopJob } = useSwrTopJobSuspend({
    workplaceSlug: workplaceSlug || '',
    slug: workplaceJobSlug || '',
    successCallback: () => {
      onTopJobSuspension?.();
    },
  });

  const handleJobActivation = async () => {
    if (eligibleForInstantActivation) {
      await instantlyActivateJob();
      return;
    }

    router.push(`/job-add/edit/${jobSlug}`);
  };

  const handleJobSuspension = () => {
    openSuspensionConfirmationModal();
  };

  const handleSuspensionConfirmation = async () => {
    await suspendJob();
  };

  const handleSuspensionCancellation = () => {
    closeSuspensionConfirmationModal();
  };

  const handleTopJobSuspension = async () => {
    if (!workplaceSlug || !workplaceJobSlug) return;

    await suspendTopJob();
  };

  return {
    handleJobActivation,
    handleJobSuspension,
    handleSuspensionConfirmation,
    handleSuspensionCancellation,
    handleTopJobSuspension,
    isActivating,
    isSuspending,
    isSuspendingTopJob,
    isSuspensionConfirmationOpen,
    openSuspensionConfirmationModal,
    closeSuspensionConfirmationModal,
  };
}
